/**
 * Service for generating and caching audio waveform peak data.
 * Fetches the mp3 version of a track, decodes it, and extracts
 * peak amplitude values for visualization.
 *
 * Memory notes (important on iPhone SE-class devices):
 * - decodeAudioData inflates audio to Float32 PCM at the DECODE CONTEXT's
 *   sample rate (~21 MB per minute of 44.1 kHz stereo). For peak extraction
 *   we don't need full fidelity, so we decode at a low sample rate
 *   (8 kHz) when the browser supports it — a ~5.5x smaller allocation —
 *   and fall back to 44.1 kHz where it doesn't.
 * - Peaks are persisted in IndexedDB so each track is downloaded/decoded at
 *   most once per device, not once per session.
 * - On iOS, very long tracks are skipped entirely (the transient decode
 *   allocation could otherwise approach Safari's per-tab memory budget and
 *   pressure CarPlay / background apps). The waveform is cosmetic; playback
 *   is unaffected.
 *
 * Cache validation (why a re-uploaded track gets a fresh waveform):
 * - Tracks are addressed by name and a re-upload writes to the same S3 key,
 *   so the name alone can't tell a new version of a file from the old one.
 *   Every persisted entry therefore records a fingerprint of the file it was
 *   computed from (S3 ETag + Last-Modified), and a lookup first asks S3 for
 *   the live file's fingerprint with a 1-byte ranged GET. Peaks are reused
 *   only when the fingerprints match; otherwise the track is re-downloaded
 *   and re-decoded. (A ranged GET rather than HEAD because the recordings
 *   bucket's CORS policy allows GET but not HEAD.)
 * - If the check itself fails — offline, locked iPhone, slow network — the
 *   cached peaks are used as-is: a possibly outdated waveform beats none,
 *   and the next successful check re-validates.
 */

import { isIOS } from '@/utils/platform';

// Low-rate decode host: 8000 Hz is the minimum the Web Audio spec allows.
const PREFERRED_DECODE_RATE = 8000;
const FALLBACK_DECODE_RATE = 44100;

// iOS: skip waveform generation for tracks longer than this (seconds).
const IOS_MAX_WAVEFORM_TRACK_SECONDS = 12 * 60;

// Upper bound on the fingerprint probe, so a stalled network can't hold a
// waveform that is already sitting in IndexedDB.
const FINGERPRINT_TIMEOUT_MS = 4000;

// IndexedDB persistence (best-effort; silently disabled where unavailable,
// e.g. private browsing or test environments).
const IDB_NAME = 'pcp-waveform-peaks';
const IDB_STORE = 'peaks';
const IDB_VERSION = 1;

class WaveformService {
  constructor() {
    this.cache = new Map(); // cacheKey -> Float32Array
    this.pendingRequest = null; // { trackName, abortController, promise }
    this._dbPromise = undefined; // lazily opened; null when unavailable
  }

  /**
   * Get normalized peak amplitudes for a track.
   * Returns cached data if available, otherwise fetches and decodes the mp3.
   * @param {string} trackName - Track name (used as cache key and to build URL)
   * @param {number} numberOfBars - Number of peak values to extract
   * @param {number|null} durationSec - Track duration in seconds, if known.
   *        Used to skip generation for very long tracks on iOS.
   * @returns {Promise<Float32Array|null>} Normalized peaks (0..1) or null on failure
   */
  async getPeaks(trackName, numberOfBars = 200, durationSec = null) {
    const cacheKey = this._cacheKey(trackName, numberOfBars);

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Cancel any in-flight request for a different track.
    // (Registered synchronously below, so a newer call can always abort an
    // older one — the IndexedDB lookup happens inside the promise chain.)
    if (this.pendingRequest && this.pendingRequest.trackName !== trackName) {
      this.pendingRequest.abortController.abort();
      this.pendingRequest = null;
    }

    // If already fetching this track, wait (don't duplicate)
    if (this.pendingRequest && this.pendingRequest.trackName === trackName) {
      return this.pendingRequest.promise;
    }

    const abortController = new AbortController();
    const promise = this._loadPeaks(trackName, numberOfBars, durationSec, abortController);
    this.pendingRequest = { trackName, abortController, promise };

    const result = await promise;
    if (this.pendingRequest && this.pendingRequest.trackName === trackName) {
      this.pendingRequest = null;
    }
    return result;
  }

  /**
   * Forget everything cached for a track — call it after uploading a new
   * version of that track so the next lookup regenerates from the live file
   * instead of serving this session's in-memory copy.
   * @param {string} trackName
   * @returns {Promise<void>} resolves once the persisted entries are gone (best-effort)
   */
  invalidate(trackName) {
    if (!trackName) return Promise.resolve();

    const prefix = trackName + '::';
    for (const key of Array.from(this.cache.keys())) {
      if (key.startsWith(prefix)) this.cache.delete(key);
    }

    // A decode that started before the upload would cache the old version.
    if (this.pendingRequest && this.pendingRequest.trackName === trackName) {
      this.pendingRequest.abortController.abort();
      this.pendingRequest = null;
    }

    return this._idbDeleteTrack(trackName);
  }

  async _loadPeaks(trackName, numberOfBars, durationSec, abortController) {
    const cacheKey = this._cacheKey(trackName, numberOfBars);
    const signal = abortController.signal;

    // Persistent cache: avoids re-downloading and re-decoding the track on
    // every visit (a full-track decode is a large transient allocation).
    const persisted = await this._idbGet(cacheKey); // { peaks, fingerprint } | null
    if (signal && signal.aborted) {
      return null;
    }

    // iOS: never inflate very long tracks to PCM just for a cosmetic
    // waveform — the allocation can destabilize the rest of the system
    // (CarPlay, navigation apps) on low-RAM devices.
    const tooLongForIOS =
      isIOS() && durationSec && durationSec > IOS_MAX_WAVEFORM_TRACK_SECONDS;
    if (!persisted && tooLongForIOS) {
      return null;
    }

    const url = this._buildMp3Url(trackName);
    if (!url) return null;

    // Which version of the file is live right now? (null when unreachable)
    const fingerprint = await this._remoteFingerprint(url, abortController);
    if (signal && signal.aborted) {
      return null;
    }

    if (persisted) {
      const unchanged =
        persisted.fingerprint !== null && persisted.fingerprint === fingerprint;
      // fingerprint === null: S3 couldn't be reached — keep what we have
      // rather than dropping the waveform; the next online lookup re-checks.
      if (unchanged || fingerprint === null) {
        this.cache.set(cacheKey, persisted.peaks);
        return persisted.peaks;
      }
      // The file was replaced since these peaks were computed.
      if (tooLongForIOS) {
        // Can't re-decode on iOS; no waveform beats the wrong waveform.
        this._idbDelete(cacheKey);
        return null;
      }
    }

    return this._fetchAndDecode(url, cacheKey, numberOfBars, fingerprint, abortController);
  }

  _cacheKey(trackName, numberOfBars) {
    return trackName + '::' + numberOfBars;
  }

  async _fetchAndDecode(url, cacheKey, numberOfBars, fingerprint, abortController) {
    try {
      // 'no-cache' makes the browser revalidate any HTTP-cached copy with S3
      // (conditional request) instead of decoding a stale one: the bucket
      // serves the files without Cache-Control, so heuristic caching would
      // otherwise apply. Unchanged files still come back from the cache.
      const response = await fetch(url, { cache: 'no-cache', signal: abortController.signal });
      if (!response.ok) return null;

      // Prefer the fingerprint of the bytes we are actually decoding.
      const decodedFingerprint = this._fingerprintFromResponse(response) || fingerprint;

      const arrayBuffer = await response.arrayBuffer();

      // Decode audio data
      const audioBuffer = await this._decodeAudio(arrayBuffer);
      if (!audioBuffer) return null;

      // Extract peaks from the first channel
      const channelData = audioBuffer.getChannelData(0);
      const peaks = this._extractPeaks(channelData, numberOfBars);

      if (peaks) {
        this.cache.set(cacheKey, peaks);
        this._idbPut(cacheKey, peaks, decodedFingerprint); // fire-and-forget
      }
      return peaks;
    } catch (error) {
      if (error.name === 'AbortError') return null;
      return null;
    }
  }

  _buildMp3Url(trackName) {
    const baseUrl = process.env.VUE_APP_RECORDINGS_URL || '';
    if (!baseUrl) return null;
    const formatted = trackName.replace(/ /g, '%20').replace(/\?/g, '%3F');
    return baseUrl + formatted + '.mp3';
  }

  // ── File fingerprinting ──

  /**
   * Identify the file currently stored at `url` without downloading it.
   * A single-range GET (bytes=0-0) returns the object's ETag and
   * Last-Modified headers with a 1-byte body; both change whenever the
   * object is replaced. Should a browser refuse to send the Range header
   * without a preflight, fall back to a plain GET and abort it as soon as
   * the headers have arrived.
   * @returns {Promise<string|null>} fingerprint, or null when unavailable
   */
  async _remoteFingerprint(url, abortController) {
    const probe = this._probeController(abortController);
    const timer = setTimeout(() => probe.controller.abort(), FINGERPRINT_TIMEOUT_MS);
    try {
      let response;
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: { Range: 'bytes=0-0' },
          cache: 'no-store',
          signal: probe.controller.signal,
        });
      } catch (error) {
        if (error.name === 'AbortError' || probe.controller.signal.aborted) return null;
        response = await fetch(url, {
          method: 'GET',
          cache: 'no-store',
          signal: probe.controller.signal,
        });
      }
      const fingerprint = this._fingerprintFromResponse(response);
      // Headers are all we need — don't let the body download.
      probe.controller.abort();
      return fingerprint;
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timer);
      probe.release();
    }
  }

  _fingerprintFromResponse(response) {
    if (!response || !response.ok) return null;
    const headers = response.headers;
    if (!headers || typeof headers.get !== 'function') return null;
    const etag = headers.get('etag') || '';
    const lastModified = headers.get('last-modified') || '';
    if (!etag && !lastModified) return null;
    return etag + '|' + lastModified;
  }

  // A controller for the probe request that also aborts when the parent
  // (per-track) controller aborts, e.g. because the user skipped tracks.
  _probeController(parentController) {
    const controller = new AbortController();
    const parentSignal = parentController && parentController.signal;
    const canListen =
      parentSignal && typeof parentSignal.addEventListener === 'function';
    const onAbort = () => controller.abort();

    if (parentSignal && parentSignal.aborted) {
      controller.abort();
    } else if (canListen) {
      parentSignal.addEventListener('abort', onAbort);
    }

    return {
      controller,
      release() {
        if (canListen) parentSignal.removeEventListener('abort', onAbort);
      },
    };
  }

  async _decodeAudio(arrayBuffer) {
    // Prefer OfflineAudioContext for decoding: it never touches the audio
    // output hardware, so iOS does not treat the page as playing
    // "interactive audio" (keeps CarPlay/Bluetooth playback clean).
    const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (OfflineCtx) {
      // First try a low-sample-rate decode host: decodeAudioData resamples
      // to the context rate, so this shrinks the decoded PCM ~5.5x. Some
      // engines reject low rates — retry at 44.1 kHz before giving up.
      // decodeAudioData detaches its input buffer, so the first attempt
      // gets a copy in case we need to retry with the original.
      const lowRate = await this._decodeWithContext(
        OfflineCtx, arrayBuffer.slice(0), PREFERRED_DECODE_RATE
      );
      if (lowRate) return lowRate;

      return this._decodeWithContext(OfflineCtx, arrayBuffer, FALLBACK_DECODE_RATE);
    }

    // Fallback (older browsers / test environment)
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      ctx.close();
      return audioBuffer;
    } catch (error) {
      return null;
    }
  }

  async _decodeWithContext(OfflineCtx, arrayBuffer, sampleRate) {
    try {
      const ctx = new OfflineCtx(1, 1, sampleRate);
      return await new Promise((resolve, reject) => {
        const result = ctx.decodeAudioData(arrayBuffer, resolve, reject);
        if (result && typeof result.then === 'function') {
          result.then(resolve, reject);
        }
      });
    } catch (error) {
      return null;
    }
  }

  _extractPeaks(channelData, numberOfBars) {
    const totalSamples = channelData.length;
    const blockSize = Math.floor(totalSamples / numberOfBars);
    if (blockSize === 0) return null;

    const peaks = new Float32Array(numberOfBars);
    for (let i = 0; i < numberOfBars; i++) {
      let max = 0;
      const start = i * blockSize;
      for (let j = 0; j < blockSize; j++) {
        const abs = Math.abs(channelData[start + j]);
        if (abs > max) max = abs;
      }
      peaks[i] = max;
    }

    // Normalize to 0..1
    let overallMax = 0;
    for (let i = 0; i < peaks.length; i++) {
      if (peaks[i] > overallMax) overallMax = peaks[i];
    }
    if (overallMax > 0) {
      for (let i = 0; i < peaks.length; i++) {
        peaks[i] /= overallMax;
      }
    }

    return peaks;
  }

  cancelPending() {
    if (this.pendingRequest) {
      this.pendingRequest.abortController.abort();
      this.pendingRequest = null;
    }
  }

  // ── IndexedDB persistence (best-effort) ──

  _openDb() {
    if (this._dbPromise !== undefined) return this._dbPromise;

    if (typeof indexedDB === 'undefined') {
      this._dbPromise = Promise.resolve(null);
      return this._dbPromise;
    }

    this._dbPromise = new Promise((resolve) => {
      try {
        const request = indexedDB.open(IDB_NAME, IDB_VERSION);
        request.onupgradeneeded = () => {
          try {
            request.result.createObjectStore(IDB_STORE);
          } catch (e) {
            // ignore — store may already exist
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
      } catch (e) {
        resolve(null);
      }
    });
    return this._dbPromise;
  }

  /**
   * @returns {Promise<{peaks: Float32Array, fingerprint: string|null}|null>}
   */
  async _idbGet(key) {
    try {
      const db = await this._openDb();
      if (!db) return null;
      return await new Promise((resolve) => {
        try {
          const tx = db.transaction(IDB_STORE, 'readonly');
          const req = tx.objectStore(IDB_STORE).get(key);
          req.onsuccess = () => resolve(this._normalizeEntry(req.result));
          req.onerror = () => resolve(null);
        } catch (e) {
          resolve(null);
        }
      });
    } catch (e) {
      return null;
    }
  }

  // Entries written before fingerprinting were bare Float32Arrays. They are
  // kept readable with an unknown (null) fingerprint, which never matches a
  // live one — so each is validated and regenerated once, then stored in
  // the new shape.
  _normalizeEntry(value) {
    if (value instanceof Float32Array) {
      return { peaks: value, fingerprint: null };
    }
    if (value && value.peaks instanceof Float32Array) {
      return {
        peaks: value.peaks,
        fingerprint: typeof value.fingerprint === 'string' ? value.fingerprint : null,
      };
    }
    return null;
  }

  async _idbPut(key, peaks, fingerprint) {
    try {
      const db = await this._openDb();
      if (!db) return;
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put({ peaks, fingerprint: fingerprint || null }, key);
    } catch (e) {
      // best-effort — persistence is an optimization only
    }
  }

  async _idbDelete(key) {
    try {
      const db = await this._openDb();
      if (!db) return;
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(key);
    } catch (e) {
      // best-effort
    }
  }

  // Remove every persisted entry for a track, whatever bar count it was
  // rendered at (keys are "<trackName>::<numberOfBars>").
  async _idbDeleteTrack(trackName) {
    try {
      const db = await this._openDb();
      if (!db) return;
      const prefix = trackName + '::';
      await new Promise((resolve) => {
        try {
          const tx = db.transaction(IDB_STORE, 'readwrite');
          const store = tx.objectStore(IDB_STORE);
          const req = store.getAllKeys();
          req.onsuccess = () => {
            for (const key of req.result || []) {
              if (
                typeof key === 'string' &&
                key.startsWith(prefix) &&
                /^\d+$/.test(key.slice(prefix.length))
              ) {
                store.delete(key);
              }
            }
          };
          tx.oncomplete = () => resolve();
          tx.onerror = () => resolve();
          tx.onabort = () => resolve();
        } catch (e) {
          resolve();
        }
      });
    } catch (e) {
      // best-effort
    }
  }
}

export default new WaveformService();
